import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Box,
  Alert,
  AlertTitle,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { Delete, Edit, Save, Visibility, VisibilityOff, Add, Close } from '@mui/icons-material';
import { GoogleMap, Polygon, Polyline } from '@react-google-maps/api';
import {
  deleteZoneAsync,
  getZonesAsync,
  getZoneTypesAsync,
  selectZones,
  setZoneState,
  updateZoneAsync,
  createZoneAsync
} from '../../redux/slices/zonesSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectCities } from '../../redux/slices/citiesSlice';
import Title from '../../components/Title';
import TableCell from '../../components/TableCell';
import ZoneModal from '../../modals/ZoneModal';
import ZonesConfigSection from '../../components/sections/ZonesConfigSection';
import { formatDate } from '../../resources/functions';
import usePermissions from '../../hooks/usePermissions';
import { zonesColors } from '../ZonesPage/constants';
import { darkMapStyles } from '../../resources/constants';
import { Zone, ZoneType } from '../../resources/types/zonesTypes';

const defaultCenter = {
  lat: 48.9225224,
  lng: 24.7103188
};

const PREFIX = 'ZoneManagementPage';

const classes = {
  title: `${PREFIX}-title`
};

const Root = styled('div')({
  [`& .${classes.title}`]: {
    marginBottom: 20
  }
});

const SNAP_THRESHOLD_METERS = 10; // метрів - радіус магнітності точок

// Функція обчислення відстані між двома координатами (Haversine formula)
const computeDistanceBetween = (point1: google.maps.LatLng, point2: google.maps.LatLng): number => {
  const R = 6371000; // Радіус Землі в метрах
  const lat1 = point1.lat() * Math.PI / 180;
  const lat2 = point2.lat() * Math.PI / 180;
  const deltaLat = (point2.lat() - point1.lat()) * Math.PI / 180;
  const deltaLng = (point2.lng() - point1.lng()) * Math.PI / 180;

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Відстань в метрах
};

const ZoneManagementPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const zonesState = useSelector(selectZones);
  const admins = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);
  const cities = useSelector(selectCities);
  
  const checkPermission = usePermissions();
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [showZones, setShowZones] = useState(true);
  const [editedZones, setEditedZones] = useState<Record<string, google.maps.LatLng[]>>({});
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    type: 'vertex' | 'zone';
    zoneId: string;
    vertexIndex?: number;
    path?: google.maps.MVCArray<google.maps.LatLng>;
  } | null>(null);
  
  // Стани для малювання нової зони
  const [showTypeSelectionDialog, setShowTypeSelectionDialog] = useState(false);
  const [selectedNewZoneType, setSelectedNewZoneType] = useState<ZoneType | null>(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [newZonePoints, setNewZonePoints] = useState<google.maps.LatLng[]>([]);
  const [cursorPosition, setCursorPosition] = useState<google.maps.LatLng | null>(null);
  
  // Стани для підтвердження видалення зони
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteCountdown, setDeleteCountdown] = useState(3);
  
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonRefsRef = useRef<Record<string, google.maps.Polygon>>({});

  useEffect(() => {
    if (!checkPermission('map')) {
      return;
    }

    dispatch(getZonesAsync(true));
    dispatch(getZoneTypesAsync());
  }, [checkPermission, dispatch]);

  // Таймер для відліку перед активацією кнопки видалення
  useEffect(() => {
    if (deleteConfirmOpen && deleteCountdown > 0) {
      const timer = setTimeout(() => {
        setDeleteCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [deleteConfirmOpen, deleteCountdown]);

  useEffect(() => {
    let nextMapCenter;

    if (auth.info?.type === 'superAdmin') {
      const city = cities.items.find(item => item._id === admins.currentCity);
      if (city?.lat && city.lon) {
        nextMapCenter = {
          lat: city.lat,
          lng: city.lon
        };
      }
    } else if (admins.info && admins.currentCity) {
      const city = admins.info.cities.find(
        item => item._id === admins.currentCity
      );
      if (city?.lat && city.lon) {
        nextMapCenter = {
          lat: city.lat,
          lng: city.lon
        };
      }
    }

    if (!nextMapCenter) {
      setMapCenter(defaultCenter);
    } else {
      setMapCenter(nextMapCenter);
    }
  }, [admins.currentCity, admins.info, auth.info, cities.items]);

  const filteredZones = useMemo(() => {
    if (auth.info?.type === 'superAdmin') {
      return zonesState.zones.filter(zone => zone.city._id === admins.currentCity);
    }
    return zonesState.zones;
  }, [zonesState.zones, admins.currentCity, auth.info]);

  const hasUnsavedChanges = Object.keys(editedZones).length > 0;

  const findNearestVertex = useCallback((latLng: google.maps.LatLng, excludeZoneId?: string): google.maps.LatLng | null => {
    let nearestVertex: google.maps.LatLng | null = null;
    let minDistance = SNAP_THRESHOLD_METERS;

    Object.entries(polygonRefsRef.current).forEach(([zoneId, polygon]) => {
      if (zoneId === excludeZoneId) return;

      const path = polygon.getPath();
      for (let i = 0; i < path.getLength(); i++) {
        const vertex = path.getAt(i);
        const distance = computeDistanceBetween(latLng, vertex);

        if (distance < minDistance) {
          minDistance = distance;
          nearestVertex = vertex;
        }
      }
    });
    
    return nearestVertex;
  }, []);

  const handlePolygonLoad = useCallback((zoneId: string, polygon: google.maps.Polygon) => {
    polygonRefsRef.current[zoneId] = polygon;
    
    // Встановлюємо координати один раз в onLoad - це робить полігон UNCONTROLLED
    // Це дозволяє Google Maps API редагувати MVCArray напряму без інтерференції React
    const zone = zonesState.zones.find(z => z._id === zoneId);
    if (zone) {
      const initialPath = zone.coordinates.map(coord => 
        new google.maps.LatLng(coord[0], coord[1])
      );
      polygon.setPath(initialPath);
    }
  }, [zonesState.zones]);

  const handlePolygonRightClick = useCallback((zoneId: string, event: google.maps.PolyMouseEvent) => {
    event.domEvent?.preventDefault();

    const polygon = polygonRefsRef.current[zoneId];
    if (!polygon) return;

    const domEvent = event.domEvent as MouseEvent;
    if (!domEvent) return;

    if (event.vertex != null) {
      const path = polygon.getPath();
      if (path.getLength() <= 3) {
        return;
      }

      setContextMenu({
        mouseX: domEvent.clientX,
        mouseY: domEvent.clientY,
        type: 'vertex',
        zoneId,
        vertexIndex: event.vertex,
        path
      });
    } else {
      setContextMenu({
        mouseX: domEvent.clientX,
        mouseY: domEvent.clientY,
        type: 'zone',
        zoneId
      });
    }
  }, []);

  const handleDeleteVertex = useCallback(() => {
    if (contextMenu?.type === 'vertex' && contextMenu.path && contextMenu.vertexIndex != null) {
      contextMenu.path.removeAt(contextMenu.vertexIndex);
    }
    setContextMenu(null);
  }, [contextMenu]);

  const handleDeleteZone = useCallback(async () => {
    if (contextMenu?.type === 'zone' && contextMenu.zoneId) {
      const zone = zonesState.zones.find(z => z._id === contextMenu.zoneId);
      if (zone) {
        setZoneToDelete({ id: zone._id, name: zone.name });
        setDeleteConfirmOpen(true);
        setDeleteCountdown(3); // Скидаємо таймер
      }
    }
    setContextMenu(null);
  }, [contextMenu, zonesState.zones]);

  const handleConfirmDelete = useCallback(async () => {
    if (!zoneToDelete) return;
    
    try {
      await dispatch(deleteZoneAsync(zoneToDelete.id));
      await dispatch(getZonesAsync(true));
      
      setEditedZones(prev => {
        const updated = { ...prev };
        delete updated[zoneToDelete.id];
        return updated;
      });
      
      setDeleteConfirmOpen(false);
      setZoneToDelete(null);
      setDeleteCountdown(3);
    } catch (error) {
      console.error('Error deleting zone:', error);
      alert('Помилка при видаленні зони');
    }
  }, [zoneToDelete, dispatch]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setZoneToDelete(null);
    setDeleteCountdown(3);
  }, []);

  const handleSaveChanges = useCallback(async () => {
    if (!hasUnsavedChanges) return;

    try {
      for (const [zoneId, coordinates] of Object.entries(editedZones)) {
        const zone = zonesState.zones.find(z => z._id === zoneId);
        if (!zone) continue;

        const newCoordinates = coordinates.map(coord => [coord.lat(), coord.lng()]);
        
        await dispatch(updateZoneAsync(zoneId, {
          ...zone,
          coordinates: newCoordinates,
          cityId: zone.city._id
        }));
      }

      setEditedZones({});
      await dispatch(getZonesAsync(true));
    } catch (error) {
      console.error('Error saving zone changes:', error);
    }
  }, [editedZones, hasUnsavedChanges, dispatch, zonesState.zones]);

  const handleDiscardChanges = useCallback(() => {
    // Відновлюємо оригінальні шляхи для всіх відредагованих зон
    Object.entries(polygonRefsRef.current).forEach(([zoneId, polygon]) => {
      const zone = zonesState.zones.find(z => z._id === zoneId);
      if (zone && polygon) {
        const originalPaths = zone.coordinates.map(coord => 
          new google.maps.LatLng(coord[0], coord[1])
        );
        polygon.setPath(originalPaths);
      }
    });
    
    // Очищуємо редаговані зони та скидаємо вибір
    setEditedZones({});
    setSelectedZoneId(null);
  }, [zonesState.zones]);

  const mapStyles = useMemo(() => 
    theme.palette.mode === 'dark' ? darkMapStyles : undefined,
    [theme.palette.mode]
  );

  // Функція початку процесу створення нової зони
  const handleStartNewZone = useCallback(() => {
    setShowTypeSelectionDialog(true);
  }, []);

  // Функція після вибору типу зони
  const handleZoneTypeSelected = useCallback((type: ZoneType) => {
    setSelectedNewZoneType(type);
    setIsDrawingMode(true);
    setNewZonePoints([]);
    setShowTypeSelectionDialog(false);
    setShowZones(true); // Показуємо існуючі зони для контексту
    setSelectedZoneId(null); // Скидаємо вибрану зону
    setEditedZones({}); // Скидаємо редаговані зони
  }, []);

  // Функція скасування малювання
  const handleCancelDrawing = useCallback(() => {
    setIsDrawingMode(false);
    setNewZonePoints([]);
    setSelectedNewZoneType(null);
    setCursorPosition(null);
  }, []);

  // Функція збереження нової зони
  const handleSaveNewZone = useCallback(async () => {
    if (newZonePoints.length < 3 || !selectedNewZoneType) {
      alert('Для створення зони потрібно мінімум 3 точки');
      return;
    }

    const coordinates = newZonePoints.map(point => [point.lat(), point.lng()]);
    
    // Генеруємо коротку назву (максимум 20 символів)
    const timestamp = Date.now().toString().slice(-6); // Останні 6 цифр timestamp
    const zoneName = `${selectedNewZoneType}_${timestamp}`;
    
    const zoneData = {
      type: selectedNewZoneType,
      coordinates,
      cityId: admins.currentCity || '',
      name: zoneName
    };
    
    console.log('Creating zone with data:', zoneData);
    
    try {
      await dispatch(createZoneAsync(zoneData));
      await dispatch(getZonesAsync(true));
      handleCancelDrawing();
      alert('Зону успішно створено!');
    } catch (error: any) {
      console.error('Error creating zone:', error);
      console.error('Error response:', error?.response?.data);
      alert(`Помилка при створенні зони: ${error?.response?.data?.message || error?.message || 'Невідома помилка'}`);
    }
  }, [newZonePoints, selectedNewZoneType, dispatch, admins.currentCity, handleCancelDrawing]);

  // Обробник кліків на карті для малювання
  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!isDrawingMode || !e.latLng) return;
    
    setNewZonePoints(prev => [...prev, e.latLng!]);
  }, [isDrawingMode]);

  // Обробник правого кліку для видалення останньої точки
  const handleMapRightClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!isDrawingMode) return;
    
    e.domEvent?.preventDefault();
    setNewZonePoints(prev => prev.slice(0, -1));
  }, [isDrawingMode]);

  // Обробник руху миші для показу preview лінії
  const handleMapMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
    if (!isDrawingMode || !e.latLng) return;
    
    setCursorPosition(e.latLng);
  }, [isDrawingMode]);

  return (
    <Root>
      <Title className={classes.title}>Зони</Title>
      
      {checkPermission('map') && (
        <>
          <Paper 
            sx={{ 
              border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', 
              borderRadius: 2,
              padding: '20px',
              marginBottom: 3
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {!isDrawingMode && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showZones}
                        onChange={(e) => setShowZones(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={showZones ? "Зони відображені" : "Зони приховані"}
                  />
                )}
                {hasUnsavedChanges && (
                  <Chip 
                    label={`Незбережені зміни: ${Object.keys(editedZones).length}`}
                    color="warning"
                    size="small"
                  />
                )}
                {isDrawingMode && (
                  <Chip 
                    label={`Малювання ${selectedNewZoneType} зони - Точок: ${newZonePoints.length}`}
                    color="success"
                    size="medium"
                    icon={<Add />}
                  />
                )}
              </Stack>
              
              <Stack direction="row" spacing={1}>
                {isDrawingMode ? (
                  <>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleCancelDrawing}
                      startIcon={<Close />}
                      size="small"
                    >
                      Скасувати
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleSaveNewZone}
                      startIcon={<Save />}
                      size="small"
                      disabled={newZonePoints.length < 3}
                    >
                      Зберегти зону
                    </Button>
                  </>
                ) : hasUnsavedChanges ? (
                  <>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleDiscardChanges}
                      size="small"
                    >
                      Скасувати
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveChanges}
                      startIcon={<Save />}
                      size="small"
                    >
                      Зберегти зміни
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleStartNewZone}
                    startIcon={<Add />}
                    size="small"
                  >
                    Намалювати нову зону
                  </Button>
                )}
              </Stack>
            </Box>

            <Box sx={{ height: 600, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter}
                zoom={13}
                options={{
                  styles: mapStyles,
                  mapTypeControl: true,
                  streetViewControl: false,
                  fullscreenControl: true,
                  gestureHandling: 'greedy',
                  clickableIcons: false
                }}
                onLoad={(map) => { mapRef.current = map; }}
                onClick={handleMapClick}
                onRightClick={handleMapRightClick}
                onMouseMove={handleMapMouseMove}
              >
                {showZones && filteredZones.map(zone => {
                  const isEdited = !!editedZones[zone._id];
                  const isSelected = selectedZoneId === zone._id;
                  
                  // НE передаємо paths prop - полігон UNCONTROLLED
                  // Координати встановлюються один раз в handlePolygonLoad через setPath()
                  // Це дозволяє Google Maps API редагувати MVCArray напряму

                  return (
                    <Polygon
                      key={zone._id}
                      options={{
                        ...zonesColors[zone.type as keyof typeof zonesColors],
                        editable: !isDrawingMode, // Вимикаємо редагування під час малювання
                        draggable: false,
                        clickable: !isDrawingMode, // Вимикаємо клік під час малювання
                        strokeColor: isSelected ? '#FF0000' : (isEdited ? '#FFA500' : zonesColors[zone.type as keyof typeof zonesColors]?.strokeColor),
                        strokeWeight: isSelected ? 2 : (isEdited ? 2 : 0),
                        strokeOpacity: isSelected ? 1 : (isEdited ? 0.8 : 0)
                      }}
                      onLoad={(polygon) => handlePolygonLoad(zone._id, polygon)}
                      onClick={() => !isDrawingMode && setSelectedZoneId(zone._id)}
                      onRightClick={(e) => !isDrawingMode && handlePolygonRightClick(zone._id, e)}
                      onMouseUp={() => {
                        if (isDrawingMode) return; // Ігноруємо під час малювання
                        const polygon = polygonRefsRef.current[zone._id];
                        if (polygon) {
                          const path = polygon.getPath();
                          
                          // Перевіряємо чи змінилися координати (порівнюємо з оригіналом)
                          const originalCoords = zone.coordinates;
                          let hasChanges = false;
                          
                          if (path.getLength() !== originalCoords.length) {
                            hasChanges = true;
                          } else {
                            for (let i = 0; i < path.getLength(); i++) {
                              const currentPoint = path.getAt(i);
                              const originalPoint = originalCoords[i];
                              
                              // Порівнюємо з точністю до 6 знаків (достатньо для Google Maps)
                              if (
                                Math.abs(currentPoint.lat() - originalPoint[0]) > 0.000001 ||
                                Math.abs(currentPoint.lng() - originalPoint[1]) > 0.000001
                              ) {
                                hasChanges = true;
                                break;
                              }
                            }
                          }
                          
                          // Якщо є зміни - застосовуємо vertex snapping і зберігаємо
                          if (hasChanges) {
                            // Застосовуємо vertex snapping до всіх точок поточної зони
                            // Якщо точка в межах 10м від точки іншої зони - вона автоматично "прилипає"
                            for (let i = 0; i < path.getLength(); i++) {
                              const vertex = path.getAt(i);
                              const nearestVertex = findNearestVertex(vertex, zone._id);
                              
                              if (nearestVertex) {
                                path.setAt(i, nearestVertex);
                              }
                            }
                            
                            // Зберігаємо відредаговані координати
                            const coordinates: google.maps.LatLng[] = [];
                            for (let i = 0; i < path.getLength(); i++) {
                              coordinates.push(path.getAt(i));
                            }
                            setEditedZones(prev => ({
                              ...prev,
                              [zone._id]: coordinates
                            }));
                          }
                        }
                      }}
                    />
                  );
                })}
                
                {/* Візуалізація нової зони під час малювання */}
                {isDrawingMode && selectedNewZoneType && newZonePoints.length > 0 && (
                  <>
                    {newZonePoints.length >= 3 ? (
                      <Polygon
                        paths={newZonePoints}
                        options={{
                          ...zonesColors[selectedNewZoneType as keyof typeof zonesColors],
                          strokeColor: '#00FF00',
                          strokeWeight: 2,
                          strokeOpacity: 1,
                          editable: false,
                          draggable: false,
                          clickable: false
                        }}
                      />
                    ) : (
                      <Polyline
                        path={newZonePoints}
                        options={{
                          strokeColor: '#00FF00',
                          strokeWeight: 3,
                          strokeOpacity: 1,
                          clickable: false
                        }}
                      />
                    )}
                    
                    {/* Preview лінія від останньої точки до курсора (тільки поки немає полігону) */}
                    {cursorPosition && newZonePoints.length > 0 && newZonePoints.length < 3 && (
                      <Polyline
                        path={[newZonePoints[newZonePoints.length - 1], cursorPosition]}
                        options={{
                          strokeColor: '#00FF00',
                          strokeOpacity: 0,
                          strokeWeight: 2,
                          clickable: false,
                          icons: [{
                            icon: {
                              path: 'M 0,-1 0,1',
                              strokeOpacity: 1,
                              strokeColor: '#00FF00',
                              scale: 2
                            },
                            offset: '0',
                            repeat: '10px'
                          }]
                        }}
                      />
                    )}
                  </>
                )}
              </GoogleMap>
            </Box>

            {isDrawingMode ? (
              <Alert severity="success" sx={{ marginTop: 2 }}>
                <AlertTitle>Малювання нової зони - {selectedNewZoneType}</AlertTitle>
                <strong>1.</strong> Клікайте лівою кнопкою миші на карті, щоб додати точки зони<br />
                <strong>2.</strong> Мінімум 3 точки для створення зони (точок зараз: {newZonePoints.length})<br />
                <strong>3.</strong> Правий клік → видаляє останню додану точку<br />
                <strong>4.</strong> Натисніть "Зберегти зону" коли закінчите (мінімум 3 точки)<br />
                <strong>5.</strong> "Скасувати" → відміна малювання
              </Alert>
            ) : (
              <Alert severity="info" sx={{ marginTop: 2 }}>
                <AlertTitle>Як працювати з зонами</AlertTitle>
                <strong>Створення нової зони:</strong><br />
                • Натисніть "Намалювати нову зону" → Виберіть тип → Клікайте на карті<br />
                <br />
                <strong>Редагування існуючих зон:</strong><br />
                • Натисніть на зону (з'явиться червоний контур)<br />
                • Перетягуйте точки для зміни форми (точки магнітяться одна до одної)<br />
                • Правий клік на точці → "Видалити точку" (мінімум 3 точки)<br />
                • Правий клік на зоні → "Видалити зону"<br />
                • "Зберегти зміни" → збереження або "Скасувати" → відміна
              </Alert>
            )}
          </Paper>

          <Paper 
            sx={{ 
              border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', 
              borderRadius: 2,
              padding: '20px'
            }}
          >
            <ZonesConfigSection
              isSuperAdmin={auth.info?.type === 'superAdmin'}
              currentCityId={admins.currentCity ?? ''}
            />
          </Paper>

          <Paper 
            sx={{ 
              border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', 
              borderRadius: 2,
              padding: '20px',
              marginTop: 3
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <Button
              color='primary'
              variant='outlined'
              onClick={() => setZoneModalOpen(true)}
            >
              Завантажити з файлу
            </Button>
          </Box>
          <TableContainer style={{ maxHeight: 440 }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Оновлений</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredZones.map(zone => (
                  <TableRow key={zone._id}>
                    <TableCell>{zone.name}</TableCell>
                    <TableCell>{zone.type}</TableCell>
                    <TableCell>
                      {formatDate(zone.updatedAt, { time: true })}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title='Редагувати'>
                          <IconButton
                            size='medium'
                            onClick={() => {
                              dispatch(
                                setZoneState({
                                  coordinates: zone.coordinates,
                                  name: zone.name,
                                  type: zone.type,
                                  cityId: zone.city._id,
                                  _id: zone._id,
                                  prohibitStop: zone.prohibitStop ?? false
                                })
                              );
                              setZoneModalOpen(true);
                            }}
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                            }}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Видалити'>
                          <IconButton
                            size='medium'
                            onClick={() => {
                              setZoneToDelete({ id: zone._id, name: zone.name });
                              setDeleteConfirmOpen(true);
                              setDeleteCountdown(3);
                            }}
                            sx={{
                              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                              borderRadius: '8px',
                              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                            }}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <ZoneModal
          open={zoneModalOpen}
          onClose={() => setZoneModalOpen(false)}
          mapCenter={mapCenter}
          zones={zonesState.zones}
        />

        <Menu
          open={contextMenu !== null}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          {contextMenu?.type === 'vertex' && (
            <MenuItem onClick={handleDeleteVertex}>
              <Delete fontSize="small" sx={{ mr: 1 }} />
              Видалити точку
            </MenuItem>
          )}
          {contextMenu?.type === 'zone' && (
            <MenuItem onClick={handleDeleteZone}>
              <Delete fontSize="small" sx={{ mr: 1 }} />
              Видалити зону
            </MenuItem>
          )}
        </Menu>

        {/* Діалог вибору типу нової зони */}
        <Dialog 
          open={showTypeSelectionDialog} 
          onClose={() => setShowTypeSelectionDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Add />
              Виберіть тип нової зони
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ paddingTop: 2 }}>
              <ToggleButtonGroup
                orientation="vertical"
                value={selectedNewZoneType}
                exclusive
                onChange={(_, value) => value && handleZoneTypeSelected(value as ZoneType)}
                fullWidth
              >
                <ToggleButton value="blue" sx={{ justifyContent: 'flex-start', padding: '12px 16px' }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '4px', 
                    bgcolor: '#5073F8', 
                    marginRight: 2 
                  }} />
                  Синя зона
                </ToggleButton>
                <ToggleButton value="green" sx={{ justifyContent: 'flex-start', padding: '12px 16px' }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '4px', 
                    bgcolor: '#4CAF50', 
                    marginRight: 2 
                  }} />
                  Зелена зона
                </ToggleButton>
                <ToggleButton value="yellow" sx={{ justifyContent: 'flex-start', padding: '12px 16px' }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '4px', 
                    bgcolor: '#fffb84', 
                    marginRight: 2 
                  }} />
                  Жовта зона
                </ToggleButton>
                <ToggleButton value="red" sx={{ justifyContent: 'flex-start', padding: '12px 16px' }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '4px', 
                    bgcolor: '#ff6363', 
                    marginRight: 2 
                  }} />
                  Червона зона
                </ToggleButton>
                <ToggleButton value="purple" sx={{ justifyContent: 'flex-start', padding: '12px 16px' }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '4px', 
                    bgcolor: '#9C27B0', 
                    marginRight: 2 
                  }} />
                  Фіолетова зона
                </ToggleButton>
                <ToggleButton value="brown" sx={{ justifyContent: 'flex-start', padding: '12px 16px' }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '4px', 
                    bgcolor: '#795548', 
                    marginRight: 2 
                  }} />
                  Коричнева зона
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowTypeSelectionDialog(false)}>
              Скасувати
            </Button>
          </DialogActions>
        </Dialog>

        {/* Діалог підтвердження видалення зони */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#F44336' }}>
              <Delete />
              Підтвердження видалення
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ paddingTop: 2 }}>
              <Alert severity="warning" sx={{ marginBottom: 2 }}>
                <AlertTitle>Увага!</AlertTitle>
                Ця дія незворотна. Зона буде видалена назавжди.
              </Alert>
              
              <Box sx={{ marginBottom: 2 }}>
                <strong>Ви впевнені, що хочете видалити зону?</strong>
              </Box>
              
              {zoneToDelete && (
                <Box 
                  sx={{ 
                    padding: 2, 
                    bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                    borderRadius: 1,
                    marginBottom: 2
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                    <span style={{ color: theme.palette.text.secondary }}>Назва:</span>
                    <strong>{zoneToDelete.name}</strong>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: theme.palette.text.secondary }}>ID:</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{zoneToDelete.id}</span>
                  </Box>
                </Box>
              )}
              
              {deleteCountdown > 0 && (
                <Alert severity="info">
                  Кнопка "Так, видалити" буде активна через {deleteCountdown} сек...
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button 
              onClick={handleCancelDelete}
              variant="outlined"
              color="inherit"
            >
              Скасувати
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color={deleteCountdown > 0 ? 'inherit' : 'error'}
              disabled={deleteCountdown > 0}
            >
              {deleteCountdown > 0 ? `Так, видалити (${deleteCountdown})` : 'Так, видалити'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
      )}
    </Root>
  );
};

export default ZoneManagementPage;
